/**
 * P&L Analysis Table for Investor Demo
 * @feature VITE_INVESTOR_DEMO
 * @task B.INV.PL - Month-by-month P&L statement
 *
 * Displays a Brazilian-style "Demonstração de Resultados" table
 * with 6 months of trailing financial data.
 */

import { useMarket } from '../../contexts/MarketContext';
import { plDemoDataUS, plDemoDataBR, PLLineItem } from '../../lib/investor-demo/pl-data';

interface PLAnalysisTableProps {
  className?: string;
}

export function PLAnalysisTable({ className = '' }: PLAnalysisTableProps) {
  const { isBrazil, formatCurrency } = useMarket();
  const data = isBrazil ? plDemoDataBR : plDemoDataUS;

  // Get revenue values for margin calculations
  const revenueValues = data.lineItems.find((i) => i.id === 'revenue')?.values ?? [];

  const calculateMargin = (values: number[], index: number): string => {
    const revenue = revenueValues[index] ?? 0;
    if (revenue === 0) return '0%';
    return `${((values[index] ?? 0) / revenue * 100).toFixed(1)}%`;
  };

  const formatCompact = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toString();
  };

  // Get the explicit background color for sticky cells (must match row background)
  const getStickyBgColor = (item: PLLineItem): string => {
    switch (item.category) {
      case 'revenue':
        return 'bg-emerald-50';
      case 'cogs':
        return 'bg-white';
      case 'gross_profit':
        return 'bg-emerald-100';
      case 'opex_header':
        return 'bg-amber-50';
      case 'opex_detail':
        return 'bg-white';
      case 'opex_total':
        return 'bg-amber-100';
      case 'ebitda':
        return 'bg-teal-100';
      case 'depreciation':
      case 'interest':
      case 'tax':
        return 'bg-white';
      case 'ebit':
        return 'bg-teal-50';
      case 'net_income':
        return 'bg-teal-600';
      default:
        return 'bg-white';
    }
  };

  const getRowStyles = (item: PLLineItem): string => {
    const baseStyles = 'transition-colors';

    switch (item.category) {
      case 'revenue':
        return `${baseStyles} bg-emerald-50 font-semibold text-gray-900`;
      case 'cogs':
        return `${baseStyles} bg-white text-gray-700`;
      case 'gross_profit':
        return `${baseStyles} bg-emerald-100 font-semibold text-emerald-800 border-b-2 border-emerald-300`;
      case 'opex_header':
        return `${baseStyles} bg-amber-50 font-medium text-amber-800`;
      case 'opex_detail':
        return `${baseStyles} bg-white text-gray-600 text-sm`;
      case 'opex_total':
        return `${baseStyles} bg-amber-100 font-medium text-amber-900 border-b border-amber-200`;
      case 'ebitda':
        return `${baseStyles} bg-teal-100 font-semibold text-teal-800 border-b-2 border-teal-300`;
      case 'depreciation':
      case 'interest':
      case 'tax':
        return `${baseStyles} bg-white text-gray-600`;
      case 'ebit':
        return `${baseStyles} bg-teal-50 font-semibold text-teal-700`;
      case 'net_income':
        return `${baseStyles} bg-teal-600 font-bold text-white`;
      default:
        return baseStyles;
    }
  };

  return (
    <div className={`rounded-xl bg-white shadow-lg overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-amber-600 to-amber-700">
        <h3 className="text-base sm:text-lg font-semibold text-white">
          {isBrazil ? 'Demonstração de Resultados' : 'Profit & Loss Statement'}
        </h3>
        <p className="text-xs sm:text-sm text-amber-100">
          {isBrazil ? 'Últimos 6 Meses' : 'Trailing 6 Months'}
        </p>
      </div>

      {/* Table - clip overflow to prevent bleeding */}
      <div className="overflow-x-auto overflow-y-hidden" style={{ clipPath: 'inset(0)' }}>
        <table className="w-full min-w-[420px]">
          <thead>
            <tr className="bg-gray-50 text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider">
              <th className="px-2 sm:px-4 py-2 sm:py-3 text-left w-28 sm:w-56 sticky left-0 bg-gray-50 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                {isBrazil ? 'Categoria' : 'Category'}
              </th>
              {data.months.map((month) => (
                <th key={month.key} className="px-2 sm:px-3 py-2 sm:py-3 text-right min-w-[70px] sm:min-w-[80px]">
                  {month.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.lineItems.map((item) => {
              // Skip header rows in value display
              if (item.isHeader) {
                return (
                  <tr key={item.id} className={getRowStyles(item)}>
                    <td
                      className={`px-2 sm:px-4 py-1.5 sm:py-2 sticky left-0 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] text-xs sm:text-sm ${getStickyBgColor(item)}`}
                      style={{ paddingLeft: `${8 + item.indent * 8}px` }}
                    >
                      {item.label}
                    </td>
                    {data.months.map((month) => (
                      <td key={month.key} className="px-2 sm:px-3 py-1.5 sm:py-2 text-right" />
                    ))}
                  </tr>
                );
              }

              return (
                <tr key={item.id} className={getRowStyles(item)}>
                  <td
                    className={`px-2 sm:px-4 py-2 sm:py-2.5 sticky left-0 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] text-xs sm:text-sm ${getStickyBgColor(item)}`}
                    style={{ paddingLeft: `${8 + item.indent * 8}px` }}
                  >
                    <span className="block truncate max-w-[100px] sm:max-w-none">{item.label}</span>
                    {item.showMargin && (
                      <span className="block text-[10px] sm:text-xs opacity-70">
                        {isBrazil ? 'Margem' : 'Margin'}
                      </span>
                    )}
                  </td>
                  {item.values.map((value, idx) => (
                    <td
                      key={data.months[idx]?.key ?? idx}
                      className="px-2 sm:px-3 py-2 sm:py-2.5 text-right tabular-nums text-xs sm:text-sm whitespace-nowrap"
                    >
                      <span className="block">
                        {item.category === 'net_income'
                          ? formatCurrency(value)
                          : `${isBrazil ? 'R$ ' : '$'}${formatCompact(value)}`}
                      </span>
                      {item.showMargin && (
                        <span className="block text-[10px] sm:text-xs opacity-70">
                          {calculateMargin(item.values, idx)}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-50 border-t border-gray-100">
        <p className="text-[10px] sm:text-xs text-gray-500 text-center">
          {isBrazil
            ? 'Dados para demonstração - Plataforma Finova'
            : 'Demo data - Finova Platform'}
        </p>
      </div>
    </div>
  );
}

export default PLAnalysisTable;
